<script setup lang="ts">
import { ChevronDownIcon, Mail01Icon } from "@gohighlevel/ghl-icons/24/outline";
import { UnitedStatesIcon } from "@gohighlevel/ghl-icons/24/misc";
import { useI18n } from "vue-i18n";

withDefaults(
  defineProps<{
    bannerSrc: string;
    bannerContainerTestId?: string;
    bannerTestId?: string;
    bannerVariant?: "embed" | "share";
    compact?: boolean;
    submitAlignment?: "full" | "right";
  }>(),
  {
    bannerContainerTestId: "ghost-form-image",
    bannerTestId: "ghost-form-banner",
    bannerVariant: "embed",
    compact: false,
    submitAlignment: "full",
  }
);

const { t } = useI18n();
</script>

<template>
  <div
    class="ghost-form-preview"
    :class="{
      'ghost-form-preview--compact': compact,
      'ghost-form-preview--share': bannerVariant === 'share',
    }"
    aria-hidden="true"
  >
    <div
      :data-testid="bannerContainerTestId"
      class="ghost-form-preview__banner"
      :class="`ghost-form-preview__banner--${bannerVariant}`"
    >
      <img
        :data-testid="bannerTestId"
        :src="bannerSrc"
        alt=""
        aria-hidden="true"
      />
    </div>

    <div class="ghost-form-preview__field">
      <span class="ghost-form-preview__label">
        {{ t("integrate.embed.ghostName") }}
      </span>
      <div class="ghost-form-preview__input">
        {{ t("integrate.embed.ghostNamePlaceholder") }}
      </div>
    </div>

    <div class="ghost-form-preview__field">
      <span class="ghost-form-preview__label">
        {{ t("integrate.embed.ghostPhone") }}
        <span class="ghost-form-preview__required">*</span>
      </span>
      <div class="ghost-form-preview__input">
        <UnitedStatesIcon class="ghost-form-preview__flag" />
        <ChevronDownIcon class="ghost-form-preview__icon" />
        <span>{{ t("integrate.embed.ghostPhonePlaceholder") }}</span>
      </div>
    </div>

    <div class="ghost-form-preview__field">
      <span class="ghost-form-preview__label">
        {{ t("integrate.embed.ghostEmail") }}
        <span class="ghost-form-preview__required">*</span>
      </span>
      <div class="ghost-form-preview__input">
        <Mail01Icon class="ghost-form-preview__icon" />
        <span>{{ t("integrate.embed.ghostEmailPlaceholder") }}</span>
      </div>
    </div>

    <div
      data-testid="ghost-form-submit"
      class="ghost-form-preview__submit"
      :class="`ghost-form-preview__submit--${submitAlignment}`"
    >
      {{ t("integrate.embed.ghostSubmit") }}
    </div>
  </div>
</template>

<style scoped>
.ghost-form-preview {
  display: flex;
  min-width: 0;
  min-height: 0;
  height: 100%;
  flex-direction: column;
  gap: 10px;
}
.ghost-form-preview__banner {
  width: 100%;
  max-width: 416px;
  aspect-ratio: 832 / 240;
  align-self: center;
  overflow: hidden;
  border-radius: 16px;
}
.ghost-form-preview__banner--share {
  aspect-ratio: 2 / 1;
}
.ghost-form-preview__banner img {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
}
.ghost-form-preview__banner--share img {
  object-position: center 70%;
}
.ghost-form-preview__banner--embed img {
  object-position: center 20%;
}
.ghost-form-preview__field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}
.ghost-form-preview__label {
  color: var(--gray-700, #344054);
  font-size: 11px;
  font-weight: 500;
  line-height: 15px;
}
.ghost-form-preview__required {
  color: var(--error-500, #f04438);
}
.ghost-form-preview__input {
  display: flex;
  min-width: 0;
  height: 32px;
  align-items: center;
  gap: 6px;
  padding: 0 9px;
  overflow: hidden;
  border: 1px solid var(--gray-300, #d0d5dd);
  border-radius: 6px;
  color: var(--gray-500, #667085);
  background: var(--base-white, #fff);
  font-size: 11px;
  white-space: nowrap;
}
.ghost-form-preview__icon {
  width: 14px;
  height: 14px;
  flex: 0 0 14px;
}
.ghost-form-preview__flag {
  display: block;
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
}
.ghost-form-preview__submit {
  display: flex;
  min-height: 34px;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  padding: 0 18px;
  border-radius: 6px;
  color: var(--base-white, #fff);
  background: var(--primary-600, #155eef);
  font-size: 12px;
  font-weight: 600;
}
.ghost-form-preview__submit--full {
  width: 100%;
}
.ghost-form-preview__submit--right {
  width: fit-content;
  align-self: flex-end;
}
.ghost-form-preview--compact {
  gap: 6px;
}
.ghost-form-preview--compact .ghost-form-preview__input {
  height: 28px;
}
.ghost-form-preview--compact .ghost-form-preview__submit {
  min-height: 30px;
}
.ghost-form-preview--share {
  gap: 12px;
}
.ghost-form-preview--share .ghost-form-preview__field {
  gap: 6px;
}
.ghost-form-preview--share .ghost-form-preview__label {
  font-size: 14px;
  line-height: 20px;
}
.ghost-form-preview--share .ghost-form-preview__input {
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: 14px;
}
.ghost-form-preview--share .ghost-form-preview__flag {
  width: 20px;
  height: 20px;
  flex-basis: 20px;
}
.ghost-form-preview--share .ghost-form-preview__icon {
  width: 16px;
  height: 16px;
  flex-basis: 16px;
}
.ghost-form-preview--share .ghost-form-preview__submit {
  min-height: 40px;
  padding: 0 24px;
  font-size: 14px;
}
</style>
